import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Slot } from 'radix-ui';
import { cn } from '../lib/cn';
import { Spinner } from './display';

/** "Apertar" sutil no toque/clique (desligado com reduzir movimento). */
const press = 'active:scale-[0.97] motion-reduce:active:scale-100';

export const buttonVariants = cva(
  cn(
    'relative inline-flex select-none items-center justify-center gap-2 whitespace-nowrap rounded-control font-[550] tracking-[-0.01em]',
    'transition-[background-color,color,border-color,box-shadow,scale,opacity] duration-150 ease-standard',
    'disabled:pointer-events-none disabled:opacity-50 aria-busy:cursor-progress [&_svg]:shrink-0',
    press,
  ),
  {
    variants: {
      variant: {
        primary: 'bg-primary text-on-primary shadow-btn hover:bg-primary-hover',
        secondary: 'border border-primary/45 bg-surface text-primary-strong shadow-control hover:border-primary hover:bg-primary-soft',
        soft: 'bg-primary-soft text-primary-strong hover:bg-primary-soft-2',
        outline: 'border border-line-strong bg-surface text-ink shadow-control hover:border-ink/25 hover:bg-surface-2',
        ghost: 'text-ink hover:bg-ink/[0.06]',
        danger: 'bg-danger-solid text-on-danger shadow-btn hover:bg-danger-hover',
        'danger-soft': 'bg-danger-soft text-danger hover:bg-danger/15',
        'danger-ghost': 'text-danger hover:bg-danger-soft',
        dark: 'bg-dark text-on-dark shadow-btn ring-1 ring-inset ring-white/10 hover:bg-dark-2',
        'on-dark': 'text-on-dark hover:bg-white/10',
        accent: 'bg-accent text-on-accent shadow-btn hover:bg-accent-hover',
        link: 'h-auto rounded-md px-0 text-primary-strong underline-offset-4 hover:underline active:scale-100',
      },
      size: {
        sm: 'h-9 px-3 text-sm [&_svg]:size-4',
        md: 'h-11 px-4 text-[15px] [&_svg]:size-[18px]',
        lg: 'h-12 px-5 text-base [&_svg]:size-5',
        xl: 'h-14 px-6 text-base [&_svg]:size-5',
      },
      block: { true: 'w-full' },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  },
);

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  /** Mostra um indicador de progresso e bloqueia novos cliques (evita venda em dobro). */
  loading?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, block, loading, leftIcon, rightIcon, asChild, children, disabled, type = 'button', ...props },
  ref,
) {
  if (asChild) {
    return (
      <Slot.Root ref={ref} className={cn(buttonVariants({ variant, size, block }), className)} {...props}>
        {children}
      </Slot.Root>
    );
  }
  return (
    <button
      ref={ref}
      type={type}
      className={cn(buttonVariants({ variant, size, block }), className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading ? <Spinner /> : leftIcon}
      {children}
      {!loading && rightIcon}
    </button>
  );
});

export const iconButtonVariants = cva(
  cn(
    'inline-flex shrink-0 items-center justify-center rounded-full transition-[background-color,color,box-shadow,scale] duration-150 ease-standard',
    'disabled:pointer-events-none disabled:opacity-40 [&_svg]:shrink-0 active:scale-[0.92] motion-reduce:active:scale-100',
  ),
  {
    variants: {
      variant: {
        ghost: 'text-ink-2 hover:bg-ink/[0.06] hover:text-ink',
        'on-dark': 'text-on-dark/90 hover:bg-white/10 hover:text-on-dark',
        soft: 'bg-primary-soft text-primary-strong hover:bg-primary-soft-2',
        primary: 'bg-primary text-on-primary shadow-btn hover:bg-primary-hover',
        outline: 'border border-line-strong bg-surface text-ink shadow-control hover:bg-surface-2',
        danger: 'text-danger hover:bg-danger-soft',
        accent: 'bg-accent text-on-accent hover:bg-accent-hover',
      },
      size: {
        sm: 'size-8 [&_svg]:size-4',
        md: 'size-10 [&_svg]:size-5',
        lg: 'size-12 [&_svg]:size-6',
      },
    },
    defaultVariants: { variant: 'ghost', size: 'md' },
  },
);

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof iconButtonVariants> {
  /** Obrigatório: descreve a ação para leitores de tela. */
  'aria-label': string;
  badge?: number | boolean;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { className, variant, size, type = 'button', badge, children, ...props },
  ref,
) {
  return (
    <button ref={ref} type={type} className={cn(iconButtonVariants({ variant, size }), 'relative', className)} {...props}>
      {children}
      {badge ? (
        <span className="absolute right-0.5 top-0.5 flex min-w-4 animate-pop items-center justify-center rounded-full bg-danger-solid px-1 text-[10px] font-bold leading-4 text-on-danger shadow-sm">
          {typeof badge === 'number' ? (badge > 99 ? '99+' : badge) : ''}
        </span>
      ) : null}
    </button>
  );
});
